import { useSafeState } from 'core/helpers/lifeCycle';
import { useStableCallback } from 'core/hooks/useStableCallback';

import MenuPopup from './MenuPopup';
import { ctx } from './context';

export type MenuAnchorProps = {
  toggle: () => void;
  setRef: (domNode: SVGElement | HTMLElement | null) => void;
};

type Props = {
  anchor: (props: MenuAnchorProps) => React.ReactNode;
  children: React.ReactNode;
};

type TState = {
  anchorNode: SVGElement | HTMLElement | null;
  isOpen: boolean;
};

const Menu = ({ children, anchor }: Props) => {
  const [{ anchorNode, isOpen }, setState] = useSafeState<TState>({
    anchorNode: null,
    isOpen: false,
  });

  const setRef = useStableCallback(
    (anchorNode: SVGElement | HTMLElement | null): void =>
      setState(st => ({ ...st, anchorNode, isOpen: st.isOpen && !!anchorNode }))
  );

  const toggle = useStableCallback((): void => {
    if (!anchorNode) {
      throw new Error(`Anchor node is empty`);
    }
    setState(st => ({ ...st, isOpen: !st.isOpen }));
  });

  const close = useStableCallback(() =>
    setState(st => ({ ...st, isOpen: false }))
  );

  return (
    <ctx.Provider value={toggle}>
      {anchor({ setRef, toggle })}
      {isOpen && anchorNode && (
        <MenuPopup anchorNode={anchorNode} close={close}>
          {children}
        </MenuPopup>
      )}
      {/* To enable <KeyBind/>s */}
      {!isOpen && <div style={{ display: 'none' }}>{children}</div>}
    </ctx.Provider>
  );
};

export default Menu;
